import { motion } from 'framer-motion';
import { useEffect, useState, type ReactNode } from 'react';
import { BottomSheet, NumPad } from '../../../components';
import { useAppData } from '../../../data';
import { cn } from '../../../lib/cn';
import { formatCurrency } from '../../../lib/currency';
import { springQuick, tapScale } from '../../../tokens';

type Field = 'bill' | 'people' | 'tip';

type Props = { open: boolean; onClose: () => void };

/**
 * Bill Splitter — three NumPad-driven inputs (bill total, people, tip %)
 * with live-updating tip / total / per-person results. Tapping a field
 * brings it active; the bottom NumPad operates on whichever field is
 * highlighted. The Done key advances to the next field; on the last
 * field it closes the sheet.
 */
export function BillSplitter({ open, onClose }: Props) {
  const data = useAppData();
  const currency = data.profile.currency;

  const [active, setActive] = useState<Field>('bill');
  const [bill, setBill] = useState(0);
  const [people, setPeople] = useState(2);
  const [tipPct, setTipPct] = useState(18);

  // Reset to defaults each time the sheet opens.
  useEffect(() => {
    if (!open) return;
    setActive('bill');
    setBill(0);
    setPeople(2);
    setTipPct(18);
  }, [open]);

  const tipAmount = bill * (tipPct / 100);
  const total = bill + tipAmount;
  const perPerson = people > 0 ? total / people : 0;

  const advance = () => {
    if (active === 'bill') setActive('people');
    else if (active === 'people') setActive('tip');
    else onClose();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Bill splitter">
      <div className="px-card-pad pt-2 pb-3 flex flex-col gap-2">
        <FieldRow
          label="Bill total"
          value={formatCurrency(bill, { currency })}
          active={active === 'bill'}
          onClick={() => setActive('bill')}
        />
        <FieldRow
          label="People"
          value={String(Math.max(1, Math.round(people)))}
          active={active === 'people'}
          onClick={() => setActive('people')}
        />
        <FieldRow
          label="Tip"
          value={`${Math.round(tipPct)}%`}
          active={active === 'tip'}
          onClick={() => setActive('tip')}
        />
      </div>

      <div className="mx-card-pad mb-3 rounded-card bg-surface-secondary p-card-pad">
        <ResultRow
          label="Tip amount"
          value={formatCurrency(tipAmount, { currency })}
        />
        <ResultRow
          label="Grand total"
          value={formatCurrency(total, { currency })}
        />
        <div className="h-px bg-text-tertiary/20 my-2" />
        <ResultRow
          label="Per person"
          value={
            <span className="text-accent text-[20px] font-mono font-light tabular-nums">
              {formatCurrency(perPerson, { currency })}
            </span>
          }
          highlighted
        />
      </div>

      {active === 'bill' ? (
        <NumPad
          key="bill"
          initialValue={bill}
          onChange={setBill}
          onDone={advance}
          doneLabel="Next"
          caption={null}
        />
      ) : null}
      {active === 'people' ? (
        <NumPad
          key="people"
          initialValue={people}
          onChange={(n) => setPeople(Math.max(1, Math.round(n)))}
          onDone={advance}
          doneLabel="Next"
          formatValue={(n) => String(Math.max(1, Math.round(n)))}
          caption={null}
        />
      ) : null}
      {active === 'tip' ? (
        <NumPad
          key="tip"
          initialValue={tipPct}
          onChange={(n) => setTipPct(Math.max(0, n))}
          onDone={advance}
          doneLabel="Done"
          formatValue={(n) => `${Math.round(n)}%`}
          caption={null}
        />
      ) : null}
    </BottomSheet>
  );
}

function FieldRow({
  label,
  value,
  active,
  onClick,
}: {
  label: string;
  value: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={tapScale}
      transition={springQuick}
      aria-pressed={active}
      className={cn(
        'w-full flex items-baseline justify-between rounded-button px-4 py-3 text-left min-h-tap',
        active ? 'bg-accent/10' : 'bg-surface-secondary',
      )}
    >
      <span
        className={cn(
          'text-[14px] font-medium',
          active ? 'text-accent' : 'text-text-secondary',
        )}
      >
        {label}
      </span>
      <span className="text-[18px] font-mono tabular-nums text-text-primary">
        {value}
      </span>
    </motion.button>
  );
}

function ResultRow({
  label,
  value,
  highlighted,
}: {
  label: string;
  value: ReactNode;
  highlighted?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between',
        highlighted ? 'pt-1' : 'py-1',
      )}
    >
      <span
        className={cn(
          'text-[13px]',
          highlighted ? 'text-text-primary font-medium' : 'text-text-secondary',
        )}
      >
        {label}
      </span>
      {typeof value === 'string' ? (
        <span className="text-[14px] font-mono tabular-nums text-text-primary">
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  );
}
